// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Composable: useApiClient
 *
 * Provides a fetch wrapper that attaches an OIDC Bearer token to every
 * request and returns the system-admin BFF base URL. Replaces the prior
 * SigV4 (aws4fetch) implementation that relied on user-entered IAM
 * credentials. See ADR-0001 / Issue #254. Development builds with no
 * Auth0 wiring send requests unauthenticated — see apiFetch.
 *
 * Usage:
 *   const { apiGet, apiPost, ... } = useApiClient();
 *   const response = await apiGet(`${getBffBaseUrl()}/tenants`);
 */

import { useAuth } from './useAuth';

/**
 * Resolve the system-admin BFF base URL.
 *
 * Production: `window.system_admin_api` is injected at deploy time by
 * `cfn_bucket_loader` (see api_endpoints.js).
 * Local dev: falls back to `VUE_APP_SYSTEM_ADMIN_BFF_URL`.
 *
 * @returns {string} BFF base URL with any trailing slashes stripped
 */
export function getBffBaseUrl() {
  const fromWindow = typeof window !== 'undefined' ? window.system_admin_api : '';
  const url = fromWindow || process.env.VUE_APP_SYSTEM_ADMIN_BFF_URL || '';
  return url.replace(/\/+$/, '');
}

/**
 * Composable that returns a Bearer-authenticated API client.
 *
 * @returns {{
 *   apiGet: (url: string) => Promise<Response>,
 *   apiPost: (url: string, body: object) => Promise<Response>,
 *   apiPut: (url: string, body: object) => Promise<Response>,
 *   apiPatch: (url: string, body: object) => Promise<Response>,
 *   apiDelete: (url: string) => Promise<Response>,
 * }}
 */
export function useApiClient() {
  const { authEnabled, getAccessToken } = useAuth();

  async function apiFetch(url, options = {}) {
    const headers = { ...(options.headers || {}) };

    // Development builds with no Auth0 wiring (AUTH0_DOMAIN unset, so the
    // auth plugin was never installed) talk to a mock BFF and have no token
    // to send. Gating on NODE_ENV as well as the plugin keeps this a
    // build-time decision: webpack folds the constant away, so a production
    // bundle always demands a token and a failure to obtain one throws
    // rather than downgrading the request to an anonymous one — which would
    // turn "not logged in" into an opaque 401 from the BFF.
    const anonymous = !authEnabled() && process.env.NODE_ENV !== 'production';

    if (!anonymous) {
      headers.Authorization = `Bearer ${await getAccessToken()}`;
    }

    return fetch(url, { ...options, headers });
  }

  async function apiGet(url) {
    return apiFetch(url, { method: 'GET' });
  }

  async function apiPost(url, body) {
    return apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async function apiPut(url, body) {
    return apiFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async function apiPatch(url, body) {
    return apiFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async function apiDelete(url) {
    return apiFetch(url, { method: 'DELETE' });
  }

  return {
    apiGet,
    apiPost,
    apiPut,
    apiPatch,
    apiDelete,
  };
}
